import React, { useEffect, useRef } from "react";
import { ChatMessage } from "../types/workflow.types";
import NovaMessage from "./NovaMessage";
import UserMessage from "./UserMessage";
import ChatInput from "./ChatInput";
import { ScrollShadow } from "@nextui-org/react";
import { NovaStore } from "../store/NovaStore";

interface ChatInterfaceProps {
  messages: ChatMessage[];
  onSendMessage: (message: string) => void;
  isTyping: boolean;
  isGenerating: boolean;
  error: string | null;
  store: NovaStore;
}

const ChatInterface: React.FC<ChatInterfaceProps> = ({
  messages,
  onSendMessage,
  isTyping,
  isGenerating,
  error,
  store,
}) => {
  const messagesEndRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (isTyping || isGenerating || messages.length > 0) {
      messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
    }
  }, [isTyping, isGenerating, messages.length]);

  // Debug: Log current state
  console.log("📁 ChatInterface - Current state:", {
    error,
  });

  return (
    <div className="relative flex flex-col w-full h-full overflow-hidden">
      {/* Background shadows */}
      <div className="pointer-events-none fixed inset-0 z-[0] overflow-hidden">
        <img
          className="absolute left-[2%] top-[20%] w-[32%]"
          src="/images/shadows/1.svg"
          alt=""
        />
        <img
          className="absolute bottom-[5%] right-[-6%] w-[48%]"
          src="/images/shadows/2.svg"
          alt=""
        />
        <img
          className="absolute bottom-[0%] left-[-12%] w-[47%]"
          src="/images/shadows/3.svg"
          alt=""
        />
      </div>
      <div className="pointer-events-none fixed w-full h-full inset-0 z-[0] overflow-hidden">
        <img
          src="/images/shadows/lines.png"
          alt=""
          className="absolute -bottom-[247px] -left-[153px]"
        />
      </div>

      {/* Header with Nova title */}
      <div className="flex items-center w-full justify-center z-[900] pt-0 pb-2">
        <div
          className="select-none bg-clip-text text-center text-[3rem]
          select-none
          bg-clip-text text-transparent uppercase leading-none tracking-[3px] px-1 font-[700]
          bg-[radial-gradient(61.17%_178.53%_at_38.83%_-13.54%,#003330_0%,#003330_15%,#003330_35%,#003330_50%,#A8F5FF_65%,#003330_85%,#003330_100%)]"
        >
          Nova
        </div>
      </div>

      <ScrollShadow className="flex-grow overflow-auto px-4 py-4 flex flex-col items-center w-full relative z-[10] gap-6">
        {messages.map((message) =>
          message.role === "user" ? (
            <UserMessage
              key={message.id}
              content={message.content}
              selectedTokens={store.selectedTokensStore.selectedTokens}
            />
          ) : (
            <NovaMessage
              key={message.id}
              content={message.content}
              selectedTokens={store.selectedTokensStore.selectedTokens}
            />
          )
        )}

        {/* Error display */}
        {error && (
          <div className="w-full max-w-[800px] p-4 bg-red-500/10 border border-red-500/20 rounded-lg">
            <div className="text-red-400 text-sm">❌ Error: {error}</div>
          </div>
        )}

        {(isTyping || isGenerating) && <NovaMessage isTyping={true} />}

        <div ref={messagesEndRef} />
      </ScrollShadow>

      {/* Chat Input - Fixed at bottom */}
      <div className="shrink-0 w-full flex justify-center relative z-30 pb-4">
        <div className="flex flex-col justify-start max-w-[800px] w-full">
          <ChatInput onSendMessage={onSendMessage} store={store} />
        </div>
      </div>
    </div>
  );
};

export default ChatInterface;
